import { Box } from "@mui/material";
import type { GridPaginationModel, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import React from "react";
import { useNavigate } from "react-router";
import { DataGrid } from "../components/data-grid";
import { PageHeader } from "../components/page-header";
import { SuiteRunsFilters } from "../components/suite-runs-filters.tsx";
import { suiteRunColumns } from "../components/suite-runs-table.tsx";
import { useDebounce } from "../hooks/use-debounce.ts";
import { usePaginatedApiQuery } from "../hooks/use-paginated-api-query.ts";
import { useUrlFilters } from "../hooks/use-url-filters.ts";
import type { TestSuiteRun } from "../types.ts";

export default function SuiteRunsPage() {
  // URL-based filter state (persists across navigation and enables sharing)
  const [filters, setFilters] = useUrlFilters({
    search: "",
    status: "",
    branch: "",
    ci_run_id: "",
    shard: "",
  });
  const nav = useNavigate();

  const {
    data,
    isLoading,
    paginationState,
    setPaginationState,
    sortModel,
    setSortModel,
    setFilters: setApiFilters,
  } = usePaginatedApiQuery<TestSuiteRun>("/suite-runs");

  const debouncedSearchQuery = useDebounce(filters.search, 300);
  const debouncedCiRunId = useDebounce(filters.ci_run_id, 300);

  // Update API filters when search query changes
  React.useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      setPaginationState((prev) => ({ ...prev, page: 0 }));
      setApiFilters((prev) => ({
        ...prev,
        q: debouncedSearchQuery,
      }));
    }
  }, [debouncedSearchQuery, setPaginationState, setApiFilters]);

  // Update API filters when status filter changes
  React.useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 0 }));
    setApiFilters((prev) => ({
      ...prev,
      run_status: filters.status || undefined,
    }));
  }, [filters.status, setPaginationState, setApiFilters]);

  // Update API filters when branch filter changes
  React.useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 0 }));
    setApiFilters((prev) => ({
      ...prev,
      git_branch: filters.branch || undefined,
    }));
  }, [filters.branch, setPaginationState, setApiFilters]);

  // Update API filters when CI run ID filter changes
  React.useEffect(() => {
    if (debouncedCiRunId !== undefined) {
      setPaginationState((prev) => ({ ...prev, page: 0 }));
      setApiFilters((prev) => ({
        ...prev,
        ci_run_id: debouncedCiRunId || undefined,
      }));
    }
  }, [debouncedCiRunId, setPaginationState, setApiFilters]);

  // Update API filters when shard filter changes
  React.useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 0 }));
    setApiFilters((prev) => ({
      ...prev,
      shard_total: filters.shard || undefined,
    }));
  }, [filters.shard, setPaginationState, setApiFilters]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationState({
      page: model.page,
      pageSize: model.pageSize,
    });
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleRowClick = (params: GridRowParams<TestSuiteRun>) => {
    nav(`/suite-runs/${params.row.id}`);
  };

  return (
    <Box data-testid="runs-page" sx={{ width: "100%", height: "100%" }}>
      <PageHeader
        title="Test Suite Runs"
        subtitle="View and analyze your test suite execution history"
      >
        <SuiteRunsFilters
          searchQuery={filters.search}
          statusFilter={filters.status}
          branchFilter={filters.branch}
          ciRunIdFilter={filters.ci_run_id}
          shardFilter={filters.shard}
          onSearchChange={(search) => setFilters({ search })}
          onStatusChange={(status) => setFilters({ status })}
          onBranchChange={(branch) => setFilters({ branch })}
          onCiRunIdChange={(ci_run_id) => setFilters({ ci_run_id })}
          onShardChange={(shard) => setFilters({ shard })}
        />
      </PageHeader>

      <DataGrid
        loading={isLoading}
        rows={data ?? []}
        columns={suiteRunColumns}
        pagination
        paginationMode="server"
        paginationModel={paginationState}
        onPaginationModelChange={handlePaginationModelChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        rowCount={-1} // Unknown row count for server-side pagination
        minHeight={`calc(100vh - 250px)`}
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
